import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Navbar";
import AdvertBanner from "@/components/AdvertBanner";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, CheckCircle2, XCircle, Clock, ArrowRight, Target, ShieldAlert, ExternalLink } from "lucide-react";
import { API } from "@/App";

const GBET_REFERRAL_URL = "https://www.gbetpools.com/create-account/referral/precious5001";

const STATUS_META = {
  won:     { label: "WON",     icon: CheckCircle2, color: "bg-emerald-600 text-white",    cardBg: "bg-emerald-50 border-emerald-200" },
  lost:    { label: "LOST",    icon: XCircle,      color: "bg-red-500 text-white",        cardBg: "bg-red-50/50 border-red-200" },
  pending: { label: "PENDING", icon: Clock,        color: "bg-amber-500 text-white",      cardBg: "bg-white border-gray-200" },
};

const FreeGamesPage = () => {
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/free-games`)
      .then(r => { setWeeks(Array.isArray(r.data) ? r.data : []); setLoading(false); })
      .catch(() => { setWeeks([]); setLoading(false); });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Header />
      <main className="max-w-5xl mx-auto px-4 pt-28 pb-10">
        <AdvertBanner />
        <div className="text-center mb-8">
          <Badge className="bg-amber-500 text-white mb-3 inline-flex items-center gap-1.5"><Trophy className="h-3 w-3" /> ARCHIVE</Badge>
          <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900">Free Games Archive</h1>
          <p className="text-gray-500 mt-2 text-sm">Every weekly free pick we&apos;ve ever published, with the actual result tracked once the pool settles.</p>
          <div className="mt-5 mx-auto max-w-2xl rounded-2xl border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-white px-5 py-4 shadow-sm" data-testid="free-games-stake-cta">
            <p className="text-sm md:text-base font-semibold text-gray-800 leading-snug">
              Stake all the <span className="text-emerald-700 font-extrabold">10 games as Perm 3 from 10</span> @ <span className="text-emerald-700 font-extrabold">₦1,000</span> unit On{" "}
              <a
                href="https://gbetpools.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-700 hover:text-blue-900 underline underline-offset-2 font-extrabold"
                data-testid="free-games-gbet-link"
              >
                Gbetpools.com
              </a>{" "}
              For a <span className="uppercase text-amber-600 font-extrabold tracking-wide">Must Win</span>.
            </p>
          </div>

          {/* Madam Precious Gbetpools referral card */}
          <div className="mt-5 mx-auto max-w-2xl" data-testid="gbet-referral-card">
            <div
              className="relative overflow-hidden rounded-3xl border-2 border-amber-400 shadow-[0_20px_50px_-12px_rgba(251,191,36,0.4)]"
              style={{
                background:
                  "linear-gradient(135deg, #052e16 0%, #065f46 45%, #b45309 100%)",
              }}
            >
              {/* Confetti sparkles */}
              <div className="pointer-events-none absolute inset-0 opacity-60">
                {[
                  { l: "8%", t: "20%", c: "#fbbf24" },
                  { l: "28%", t: "78%", c: "#f472b6" },
                  { l: "55%", t: "16%", c: "#34d399" },
                  { l: "78%", t: "70%", c: "#60a5fa" },
                  { l: "92%", t: "26%", c: "#fbbf24" },
                ].map((d, i) => (
                  <span
                    key={i}
                    className="absolute h-2 w-2 rounded-full animate-pulse"
                    style={{ left: d.l, top: d.t, background: d.c, animationDelay: `${i * 250}ms` }}
                  />
                ))}
              </div>
              <div className="pointer-events-none absolute -top-20 -right-20 h-52 w-52 rounded-full bg-amber-400/25 blur-3xl" />

              <div className="relative z-10 px-5 sm:px-7 py-6 text-white text-left">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 text-[10px] sm:text-xs font-extrabold tracking-widest uppercase text-amber-200 bg-amber-500/15 border border-amber-300/40 rounded-full px-3 py-1">
                    <Trophy className="h-3.5 w-3.5" /> Madam Precious&apos; Referral
                  </span>
                </div>

                <h3 className="text-lg sm:text-2xl font-extrabold leading-tight mb-2" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.35)" }}>
                  Want to play my 10 free draw games and WIN even if 7 cut?
                </h3>
                <p className="text-sm sm:text-base text-emerald-50/90 leading-relaxed">
                  Register here, fund your account and stake the games. Only bets placed with my referral link count toward the win guarantee.
                </p>

                {/* Referral CTA */}
                <a
                  href={GBET_REFERRAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group mt-4 inline-flex items-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 text-emerald-950 font-extrabold px-6 py-3 shadow-lg shadow-amber-500/30 transition-transform hover:scale-[1.03] active:scale-100"
                  data-testid="gbet-referral-cta"
                >
                  Register on Gbetpools with my link
                  <ExternalLink className="h-4 w-4" />
                </a>
                <p className="mt-2 text-[11px] text-emerald-100/80 font-mono break-all">
                  {GBET_REFERRAL_URL}
                </p>

                {/* Warning strip */}
                <div className="mt-4 flex items-start gap-2 rounded-xl bg-red-500/15 border border-red-400/40 px-3 py-2.5">
                  <ShieldAlert className="h-4 w-4 text-red-200 shrink-0 mt-0.5" />
                  <p className="text-xs sm:text-sm text-red-100 leading-snug">
                    <span className="font-extrabold uppercase tracking-wide">Important:</span>{" "}
                    If you don&apos;t see my referral link,{" "}
                    <span className="underline decoration-red-300 underline-offset-2 font-bold">don&apos;t register</span>.
                    You are only sure of winning when you register with my support link{" "}
                    <span className="font-bold">AND</span> stake the games on gbetpools.com.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <p className="text-center text-gray-400 py-12">Loading…</p>
        ) : weeks.length === 0 ? (
          <Card className="text-center py-12 border-dashed">
            <CardContent>
              <Trophy className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No free pick archive yet. Generate the weekly Draws Post in admin to start populating this page.</p>
              <Link to="/" className="inline-flex items-center gap-1 mt-4 text-emerald-600 font-bold hover:underline">
                Back to Home <ArrowRight className="h-4 w-4" />
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {weeks.map(w => (
              <section key={w.week_number} data-testid={`free-games-week-${w.week_number}`}>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <h2 className="text-xl md:text-2xl font-extrabold text-gray-900 flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-amber-500" /> Week {w.week_number}
                    {w.coupon_color && <Badge className="bg-gray-200 text-gray-800 capitalize">{w.coupon_color}</Badge>}
                  </h2>
                  <div className="flex items-center gap-2 text-xs">
                    <Badge className="bg-emerald-100 text-emerald-700">✓ {w.won_count} won</Badge>
                    <Badge className="bg-red-100 text-red-700">✗ {w.lost_count} lost</Badge>
                    {w.pending_count > 0 && <Badge className="bg-amber-100 text-amber-700">⏳ {w.pending_count} pending</Badge>}
                    <span className="text-gray-400">of {w.total}</span>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {w.picks.map(p => {
                    const meta = STATUS_META[p.status] || STATUS_META.pending;
                    const Icon = meta.icon;
                    return (
                      <Card key={p.pick_id} className={`border ${meta.cardBg}`} data-testid={`fg-pick-${p.pick_id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                            <Badge className={`${meta.color} text-xs inline-flex items-center gap-1`}>
                              <Icon className="h-3 w-3" /> {meta.label}
                            </Badge>
                            {p.coupon_number && (
                              <Badge className="bg-gray-900 text-white text-xs">Coupon #{p.coupon_number}</Badge>
                            )}
                          </div>
                          <p className="font-bold text-gray-900">{p.home_team} vs {p.away_team}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-xs text-gray-500">{p.league}</span>
                            {p.kickoff && <span className="text-xs text-amber-600">{p.kickoff}</span>}
                          </div>
                          <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-1">
                              <Target className="h-3.5 w-3.5 text-green-600" />
                              <span className="text-sm font-semibold text-green-700">FT Draw</span>
                            </div>
                            {p.probability_pct && (
                              <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                                {p.probability_pct}%
                              </span>
                            )}
                          </div>
                          {p.status === "won" && p.result_score && (
                            <p className="mt-2 text-xs text-emerald-700 font-bold">Final score: {p.result_score}</p>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default FreeGamesPage;
